'use client';

import { unstable_serialize, type Middleware } from 'swr';

// predic 子树 GET 请求的本地缓存中间件：请求成功时写入 localStorage，
// 请求失败时回退到未过期的缓存数据（stale-if-error）。
// 经 src/app/predic/PredicLayoutClient.tsx 的嵌套 SWRConfig 挂载，仅作用于该子树；
// 追加在全局 storeMiddleware/apiMiddleware 之后，包装的是各 hook 最终解析出的 fetcher。

/** 仅在生产域名下启用本地缓存；其余域名（测试环境/webview 等）直接透传，不读写 localStorage */
const CACHE_ENABLED_ORIGIN = 'https://chat.pythra.ai';

function isCacheEnabledOrigin(): boolean {
  return typeof window !== 'undefined' && window.location.origin === CACHE_ENABLED_ORIGIN;
}

const PREFIX = 'predic-swr-cache:';
/** 响应结构变更时递增，旧缓存整体失效 */
const VERSION = 1;
const TTL_MS = 24 * 60 * 60 * 1000;
/** 单条上限，超大响应（行情、长列表页）不落盘，避免挤爆 localStorage */
const MAX_ENTRY_CHARS = 200 * 1024;

/**
 * 不缓存的数组 key（首元素标签）。
 * 新增直连第三方域名的 hook 时，需把它的 key 标签加进来。
 */
const EXCLUDED_KEY_TAGS = new Set([
  // 实时报价，过期即失真（经 /api/polymarket/quote 代理）
  'polymarket-withdraw-quote',
  // 链上 RPC 直查的实时余额
  'onchain-balance',
  // 第三方域名直连（Polymarket data-api / user-pnl-api / gamma-api / bridge）
  'poly-leaderboard',
  'poly-pnl',
  'poly-position',
  'poly-value',
  'polymarket-supported-assets',
]);

interface CacheEntry {
  v: number;
  cachedAt: number;
  data: unknown;
}

/** 缓存按用户隔离，避免切换账号串数据；未登录归入 anon */
function currentUserId(): string {
  try {
    const raw = localStorage.getItem('currentUserInfo');
    if (!raw) return 'anon';
    const id = (JSON.parse(raw) as { id?: number | string })?.id;
    return id != null ? String(id) : 'anon';
  } catch {
    return 'anon';
  }
}

function shouldCache(key: unknown): boolean {
  if (typeof key === 'string') {
    // 只缓存本项目后端（api:）；twitter:/dexapi: 为第三方域名，
    // 无前缀字符串 key 是 storeMiddleware 的 localStorage 状态，均不缓存
    return key.startsWith('api:');
  }
  if (Array.isArray(key) && key.length > 0) {
    return !(typeof key[0] === 'string' && EXCLUDED_KEY_TAGS.has(key[0]));
  }
  return false;
}

function cacheKeyFor(key: unknown): string {
  return `${PREFIX}${currentUserId()}:${unstable_serialize(key as Parameters<typeof unstable_serialize>[0])}`;
}

function saveCache(cacheKey: string, data: unknown) {
  try {
    const entry: CacheEntry = { v: VERSION, cachedAt: Date.now(), data };
    const raw = JSON.stringify(entry);
    if (raw.length > MAX_ENTRY_CHARS) return;
    localStorage.setItem(cacheKey, raw);
  } catch {
    // 序列化失败 / QuotaExceeded 时静默跳过，不影响正常请求
  }
}

function loadCache(cacheKey: string): unknown | undefined {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return undefined;
    const entry = JSON.parse(raw) as CacheEntry;
    if (entry.v !== VERSION || Date.now() - entry.cachedAt > TTL_MS) {
      localStorage.removeItem(cacheKey);
      return undefined;
    }
    return entry.data;
  } catch {
    return undefined;
  }
}

/** 清空本命名空间全部缓存；登出 / 401 时调用 */
export function clearLocalSwrCache() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // localStorage 不可用（SSR/隐私模式）时忽略
  }
}

type AnyFetcher = (...args: unknown[]) => unknown;

export const localCacheMiddleware: Middleware = (useSWRNext) => (key, fetcher, config) => {
  // 是否缓存要看"已解析的 key"（fetcher 首参）：useSWRInfinite 传入中间件的
  // key 是 getKey 函数，只有各页 fetcher 收到的才是真实数组 key，因此统一
  // 包装 fetcher、调用时再判定。
  const wrapped = fetcher
    ? async (...args: unknown[]) => {
        // useSWRMutation 的 trigger 第二参带 { arg } —— 写操作直接放行不缓存
        const payload = args[1];
        const resolvedKey = args[0];
        if (
          !isCacheEnabledOrigin() ||
          (payload && typeof payload === 'object' && 'arg' in payload) ||
          !shouldCache(resolvedKey)
        ) {
          return (fetcher as AnyFetcher)(...args);
        }
        const cacheKey = cacheKeyFor(resolvedKey);
        try {
          const data = await (fetcher as AnyFetcher)(...args);
          saveCache(cacheKey, data);
          return data;
        } catch (error) {
          const cached = loadCache(cacheKey);
          if (cached !== undefined) return cached;
          throw error;
        }
      }
    : fetcher;
  return useSWRNext(key, wrapped as typeof fetcher, config);
};

export default localCacheMiddleware;
